import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { interpolate, messages } from './translations';

const STORAGE_KEY = 'lpb-language';
const I18nContext = createContext(null);

const resolveInitialLanguage = () => {
  if (typeof window === 'undefined') return 'fr';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'fr' || stored === 'en') return stored;
  return String(window.navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'fr';
};

const getNested = (source, path) => path.split('.').reduce((value, part) => value?.[part], source);

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }
  if (typeof value?.seconds === 'number') {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  const converted = value instanceof Date ? value : new Date(value);
  return Number.isNaN(converted.getTime()) ? null : converted;
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(resolveInitialLanguage);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = language;
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState(nextLanguage === 'en' ? 'en' : 'fr');
  }, []);

  const t = useCallback((key, variables = {}) => {
    const translated = getNested(messages[language], key);
    const fallback = getNested(messages.fr, key);
    return interpolate(translated ?? fallback ?? key, variables);
  }, [language]);

  const locale = language === 'en' ? 'en-GB' : 'fr-FR';

  const pick = useCallback((french, english) => (
    language === 'en' ? english : french
  ), [language]);

  const formatNumber = useCallback((value, options = {}) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return t('common.notDefined');
    return new Intl.NumberFormat(locale, options).format(parsed);
  }, [locale, t]);

  const formatCurrency = useCallback((value, currency = 'EUR') => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return t('common.notDefined');
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(parsed);
  }, [locale, t]);

  const formatDate = useCallback((value, options = {}) => {
    const date = toDate(value);
    if (!date) return options.fallback || t('common.notSpecified');
    const { fallback, ...intlOptions } = options;
    return new Intl.DateTimeFormat(locale, Object.keys(intlOptions).length ? intlOptions : undefined).format(date);
  }, [locale, t]);

  const value = useMemo(() => ({
    language,
    locale,
    setLanguage,
    t,
    pick,
    formatNumber,
    formatCurrency,
    formatDate,
  }), [formatCurrency, formatDate, formatNumber, language, locale, pick, setLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside I18nProvider.');
  return value;
};
